import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

import StatsCard from '../../components/common/StatsCard';
import PageHeader from '../../components/common/PageHeader';
import {
  Users,
  DollarSign,
  Briefcase,
  TrendingUp,
  Pause,
  Play,
  Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

type ProviderStrategySettings = {
  strategyName: string;
  description: string;
  minDeposit: string;
  performanceFee: string;
  isPaused: boolean;
};

const initialStrategy: ProviderStrategySettings = {
  strategyName: 'Gold Scalper Pro',
  description:
    'High-frequency gold trading strategy with tight risk control and automated algorithmic execution.',
  minDeposit: '100',
  performanceFee: '15',
  isPaused: false,
};

const ProviderDashboard = () => {
  const [strategy, setStrategy] =
    useState<ProviderStrategySettings>(initialStrategy);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await new Promise(r => setTimeout(r, 600));
    setIsSaving(false);
    toast.success(
      'Strategy details and fee configuration updated successfully!'
    );
  };

  const togglePause = () => {
    const nextPaused = !strategy.isPaused;
    setStrategy(prev => ({ ...prev, isPaused: nextPaused }));
    toast.success(
      `Strategy ${nextPaused ? 'paused' : 'resumed'} for new investors`
    );
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4 flex items-center justify-between">
        <PageHeader
          title="Strategy Provider Dashboard"
          description="Monitor your strategy followers, accumulated performance fee revenue, and manage strategy parameters"
        />
        <Button
          variant={strategy.isPaused ? 'default' : 'outline'}
          onClick={togglePause}
        >
          {strategy.isPaused ? (
            <>
              <Play className="h-4 w-4 mr-2 text-green-600" />
              Resume Strategy
            </>
          ) : (
            <>
              <Pause className="h-4 w-4 mr-2 text-amber-600" />
              Pause Strategy
            </>
          )}
        </Button>
      </div>

      {/* Provider Stats Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        <StatsCard
          title="Total Followers"
          value="45"
          change="Active copiers"
          icon={Users}
        />
        <StatsCard
          title="Total Revenue"
          value="$12,450.00"
          change="Performance fees"
          icon={DollarSign}
        />
        <StatsCard
          title="Monthly Revenue"
          value="$1,850.00"
          change="This Month"
          icon={TrendingUp}
        />
        <StatsCard
          title="Total AUM"
          value="$125,000.00"
          change="Assets Under Management"
          icon={Briefcase}
        />
        <StatsCard
          title="Active Investors"
          value="42"
          change="Currently copying"
          icon={Users}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Strategy Management Form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Edit Strategy & Fee Parameters</CardTitle>
            <CardDescription>
              Manage parameters displayed to prospective copiers in the Strategy
              Marketplace
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4 text-sm">
              <div className="space-y-2">
                <Label htmlFor="strategyName">Strategy Title</Label>
                <Input
                  id="strategyName"
                  value={strategy.strategyName}
                  onChange={e =>
                    setStrategy(prev => ({
                      ...prev,
                      strategyName: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Strategy Description</Label>
                <textarea
                  id="description"
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={strategy.description}
                  onChange={e =>
                    setStrategy(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minDeposit">
                    Minimum Required Deposit ($)
                  </Label>
                  <Input
                    id="minDeposit"
                    type="number"
                    value={strategy.minDeposit}
                    onKeyDown={preventInvalidNumberInput}
                    onPaste={preventInvalidNumberPaste}
                    onChange={e =>
                      setStrategy(prev => ({
                        ...prev,
                        minDeposit: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="performanceFee">Performance Fee (%)</Label>
                  <Input
                    id="performanceFee"
                    type="number"
                    value={strategy.performanceFee}
                    onKeyDown={preventInvalidNumberInput}
                    onPaste={preventInvalidNumberPaste}
                    onChange={e =>
                      setStrategy(prev => ({
                        ...prev,
                        performanceFee: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button type="submit" disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving Changes...' : 'Save Strategy Details'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Quick Revenue Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue Share Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-xs text-muted-foreground">
            <p>
              Performance fees are calculated automatically at the end of each
              billing cycle using high-water mark logic.
            </p>
            <div className="p-3 border rounded-md bg-muted/20 space-y-1">
              <span className="font-semibold text-foreground block">
                High-Water Mark Protection
              </span>
              <p>
                Fees are charged only on net new profits generated above the
                previous peak equity.
              </p>
            </div>
            <div className="p-3 border rounded-md bg-muted/20 space-y-1">
              <span className="font-semibold text-foreground block">
                Payout Schedule
              </span>
              <p>
                Performance fee earnings are credited directly to your primary
                wallet on the 1st of every month.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProviderDashboard;
